import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Wallet, TrendingUp, TrendingDown, Activity, 
  DollarSign, BarChart3, Clock, ArrowUpRight, 
  ArrowDownRight, Loader2, RefreshCw, History,
  Wifi, WifiOff, Bell, BellOff
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, subValue, color = "blue" }) => {
  const colors = {
    blue: "text-[#3B82F6] bg-[#3B82F6]/10",
    green: "text-green-400 bg-green-400/10",
    orange: "text-[#F97316] bg-[#F97316]/10",
    purple: "text-purple-400 bg-purple-400/10"
  };
  
  return (
    <div className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <span className="text-gray-400 text-sm">{label}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      {subValue && <div className="text-sm text-gray-500 mt-1">{subValue}</div>}
    </div>
  );
};

const PositionCard = ({ position, onClick }) => {
  const isProfit = position.pnl >= 0;
  
  return (
    <div 
      onClick={onClick}
      className="bg-[#131b2e] rounded-xl border border-[#1e2a45] p-4 hover:border-[#3B82F6]/50 transition-colors cursor-pointer"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <h4 className="text-white font-medium text-sm truncate">{position.market_title}</h4>
          <div className="flex items-center gap-2 mt-1">
            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
              position.outcome === 'YES' 
                ? 'bg-[#3B82F6]/20 text-[#3B82F6]' 
                : 'bg-[#F97316]/20 text-[#F97316]'
            }`}>
              {position.outcome}
            </span>
            <span className="text-gray-500 text-xs">{position.quantity} contracts</span>
          </div>
        </div>
        <div className={`flex items-center gap-1 ${isProfit ? 'text-green-400' : 'text-red-400'}`}>
          {isProfit ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
          <span className="text-sm font-medium">{position.pnl_percent.toFixed(1)}%</span>
        </div>
      </div>
      
      <div className="grid grid-cols-3 gap-3 text-xs">
        <div>
          <span className="text-gray-500 block">Avg Price</span>
          <span className="text-white">{position.avg_price.toFixed(0)}TK¢</span>
        </div>
        <div>
          <span className="text-gray-500 block">Current</span>
          <span className="text-white">{position.current_price.toFixed(0)}TK¢</span>
        </div>
        <div>
          <span className="text-gray-500 block">P&L</span>
          <span className={isProfit ? 'text-green-400' : 'text-red-400'}>
            {isProfit ? '+' : ''}{position.pnl.toFixed(2)}
          </span>
        </div>
      </div>
    </div>
  );
};

const TradeRow = ({ trade }) => {
  const isBuy = trade.side === 'BUY';
  
  return (
    <div className="flex items-center justify-between py-3 border-b border-[#1e2a45] last:border-0">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${isBuy ? 'bg-green-400/10' : 'bg-red-400/10'}`}>
          {isBuy ? (
            <ArrowUpRight className="h-4 w-4 text-green-400" />
          ) : (
            <ArrowDownRight className="h-4 w-4 text-red-400" />
          )}
        </div>
        <div>
          <div className="text-white text-sm font-medium">
            {trade.side} {trade.quantity} {trade.outcome}
          </div>
          <div className="text-gray-500 text-xs truncate max-w-[200px]">
            {trade.market_title}
          </div>
        </div>
      </div>
      <div className="text-right">
        <div className={`text-sm font-medium ${isBuy ? 'text-red-400' : 'text-green-400'}`}>
          {isBuy ? '-' : '+'}{trade.total_cost?.toFixed(2) || '0.00'} TK$
        </div>
        <div className="text-gray-500 text-xs">
          {trade.created_at ? new Date(trade.created_at).toLocaleDateString() : ''}
        </div>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const [summary, setSummary] = useState(null);
  const [positions, setPositions] = useState([]);
  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('positions');
  
  // WebSocket state
  const [wsConnected, setWsConnected] = useState(false);
  const [realtimeUpdates, setRealtimeUpdates] = useState([]);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Get WebSocket URL from API URL
  const getWsUrl = useCallback(() => {
    const url = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    return `${url}/api/ws`;
  }, []);

  // Connect to WebSocket
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    try {
      const ws = new WebSocket(getWsUrl());
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        setWsConnected(true);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'price_update') {
            // Update positions with new prices
            setPositions(prev => prev.map(pos => {
              if (pos.market_id === data.market_id) {
                const newPrice = pos.outcome === 'YES' 
                  ? data.prices.YES_price_bp / 100 
                  : data.prices.NO_price_bp / 100;
                const pnl = pos.quantity * (newPrice / 100) - pos.cost_basis;
                const pnlPercent = pos.cost_basis > 0 ? (pnl / pos.cost_basis) * 100 : 0;
                
                return {
                  ...pos,
                  current_price: newPrice,
                  current_value: pos.quantity * (newPrice / 100),
                  pnl,
                  pnl_percent: pnlPercent
                };
              }
              return pos;
            }));

            // Add to realtime updates feed
            setRealtimeUpdates(prev => [{
              type: 'price',
              market_id: data.market_id,
              prices: data.prices,
              timestamp: new Date().toISOString()
            }, ...prev].slice(0, 10));
          }
          
          if (data.type === 'trade') {
            setRealtimeUpdates(prev => [{
              type: 'trade',
              market_id: data.market_id,
              trade: data.trade,
              timestamp: new Date().toISOString()
            }, ...prev].slice(0, 10));
          }
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setWsConnected(false);
        // Attempt to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 5000);
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        ws.close();
      };

      wsRef.current = ws;
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, [getWsUrl]);

  // Cleanup WebSocket on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDashboardData();
      connectWebSocket();
    }
    // fetchDashboardData is stable per-mount; connectWebSocket is memoized.
    // Adding fetchDashboardData to deps would require memoizing it too and
    // widens the closure for no user-visible gain — the effect only fires
    // on auth transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, connectWebSocket]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // Fetch from both old dashboard API and new orderbook API
      const [summaryData, oldPositionsData, tradesData, orderbookPositions, orderbookOrders] = await Promise.all([
        apiService.getDashboardSummary(),
        apiService.getPositions(),
        apiService.getTrades(10),
        // New orderbook positions
        fetch(`${API_URL}/api/orderbook/user/positions`, { 
          headers, 
          credentials: 'include' 
        }).then(r => r.ok ? r.json() : { positions: [] }).catch(() => ({ positions: [] })),
        // New orderbook orders (for open orders count)
        fetch(`${API_URL}/api/orderbook/user/orders`, { 
          headers, 
          credentials: 'include' 
        }).then(r => r.ok ? r.json() : { orders: [] }).catch(() => ({ orders: [] }))
      ]);
      
      setSummary(summaryData);
      
      // Combine positions from both old AMM system and new orderbook
      const combinedPositions = [
        ...(oldPositionsData.positions || []),
        ...(orderbookPositions.positions || []).map(p => ({
          id: p.id,
          market_id: p.market_id,
          market_title: p.market_title || 'Market',
          market_status: p.market_status || 'OPEN',
          outcome: p.outcome,
          quantity: p.shares || p.quantity || 0,
          avg_price: p.avg_price || 50,
          current_price: 50, // Will be updated by WebSocket
          current_value: (p.shares || 0) * 0.5,
          cost_basis: (p.shares || 0) * (p.avg_price || 50) / 100,
          pnl: 0,
          pnl_percent: 0,
          source: 'orderbook'
        }))
      ];
      
      // Deduplicate by market_id and outcome
      const uniquePositions = combinedPositions.reduce((acc, pos) => {
        const key = `${pos.market_id}-${pos.outcome}`;
        if (!acc[key] || pos.source === 'orderbook') {
          acc[key] = pos;
        }
        return acc;
      }, {});
      
      setPositions(Object.values(uniquePositions).filter(p => p.quantity > 0));
      setTrades(tradesData.trades || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-[#050810]">
        <Header />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
          <span className="ml-3 text-gray-400">Loading dashboard...</span>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810]">
      <Header />
      
      {/* Header */}
      <div className="bg-gradient-to-b from-[#131b2e] to-[#050810] border-b border-[#1e2a45]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                Dashboard
                {/* WebSocket Status Indicator */}
                <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                  wsConnected 
                    ? 'bg-green-500/20 text-green-400' 
                    : 'bg-red-500/20 text-red-400'
                }`}>
                  {wsConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                  {wsConnected ? 'Live' : 'Offline'}
                </span>
              </h1>
              <p className="text-gray-400 text-sm mt-1">
                Welcome back, {summary?.user?.username || user?.email?.split('@')[0]}
              </p>
            </div>
            <Button
              onClick={fetchDashboardData}
              variant="outline"
              className="border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45]"
              data-testid="refresh-dashboard"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={Wallet}
            label="Wallet Balance"
            value={summary?.wallet?.formatted_balance || 'TK$0.00'}
            subValue="Available to trade"
            color="blue"
          />
          <StatCard
            icon={BarChart3}
            label="Portfolio Value"
            value={`TK$${((summary?.portfolio?.total_value || 0) / 100).toFixed(2)}`}
            subValue={`${summary?.portfolio?.positions_count || 0} open positions`}
            color="green"
          />
          <StatCard
            icon={TrendingUp}
            label="Positions Value"
            value={`TK$${((summary?.portfolio?.positions_value || 0) / 100).toFixed(2)}`}
            subValue="Current market value"
            color="orange"
          />
          <StatCard
            icon={Activity}
            label="Total Trades"
            value={summary?.portfolio?.trades_count || 0}
            subValue="All time"
            color="purple"
          />
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Positions/Trades */}
          <div className="lg:col-span-2">
            <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] overflow-hidden">
              {/* Tabs */}
              <div className="flex border-b border-[#1e2a45]">
                <button
                  onClick={() => setActiveTab('positions')}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'positions'
                      ? 'text-white border-b-2 border-[#3B82F6] bg-[#1e2a45]/30'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <BarChart3 className="h-4 w-4 inline mr-2" />
                  Open Positions ({positions.length})
                </button>
                <button
                  onClick={() => setActiveTab('trades')}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                    activeTab === 'trades'
                      ? 'text-white border-b-2 border-[#3B82F6] bg-[#1e2a45]/30'
                      : 'text-gray-400 hover:text-white'
                  }`}
                >
                  <History className="h-4 w-4 inline mr-2" />
                  Trade History
                </button>
              </div>

              {/* Content */}
              <div className="p-4">
                {activeTab === 'positions' ? (
                  positions.length > 0 ? (
                    <div className="grid gap-3">
                      {positions.map((position) => (
                        <PositionCard 
                          key={position.id} 
                          position={position}
                          onClick={() => navigate(`/?market=${position.market_id}`)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <BarChart3 className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-white mb-1">No Open Positions</h3>
                      <p className="text-gray-400 text-sm mb-4">Start trading to build your portfolio</p>
                      <Button
                        onClick={() => navigate('/')}
                        className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"
                      >
                        Browse Markets
                      </Button>
                    </div>
                  )
                ) : (
                  trades.length > 0 ? (
                    <div>
                      {trades.map((trade) => (
                        <TradeRow key={trade.id} trade={trade} />
                      ))}
                      <div className="mt-4 pt-4 border-t border-[#1e2a45] text-center">
                        <Button
                          onClick={() => navigate('/trading')}
                          variant="outline"
                          className="border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45]"
                        >
                          <History className="h-4 w-4 mr-2" />
                          View Full Trading History
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <History className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                      <h3 className="text-lg font-semibold text-white mb-1">No Trades Yet</h3>
                      <p className="text-gray-400 text-sm">Your trade history will appear here</p>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Wallet Card */}
            <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Wallet className="h-5 w-5 text-[#3B82F6]" />
                  Wallet
                </h3>
              </div>
              
              <div className="bg-gradient-to-br from-[#3B82F6]/20 to-[#F97316]/20 rounded-xl p-4 mb-4">
                <span className="text-gray-400 text-xs block mb-1">Available Balance</span>
                <span className="text-3xl font-bold text-white">
                  {summary?.wallet?.formatted_balance || 'TK$0.00'}
                </span>
              </div>
              
              <div className="text-xs text-gray-500 text-center">
                Play money for simulation only
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-5">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-green-400" />
                Quick Stats
              </h3>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-[#1e2a45]">
                  <span className="text-gray-400 text-sm">Open Positions</span>
                  <span className="text-white font-medium">{positions.length}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-[#1e2a45]">
                  <span className="text-gray-400 text-sm">Total Trades</span>
                  <span className="text-white font-medium">{summary?.portfolio?.trades_count || 0}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-400 text-sm">Account Status</span>
                  <span className={`text-sm font-medium ${summary?.user?.is_subscribed ? 'text-green-400' : 'text-gray-400'}`}>
                    {summary?.user?.is_subscribed ? 'Pro' : 'Free'}
                  </span>
                </div>
              </div>
            </div>

            {/* Real-time Activity Feed */}
            {wsConnected && realtimeUpdates.length > 0 && (
              <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-5">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Activity className="h-5 w-5 text-[#3B82F6]" />
                  Live Activity
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                </h3>
                
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {realtimeUpdates.slice(0, 5).map((update) => (
                    <div key={`${update.type}-${update.timestamp}`} className="text-xs p-2 bg-[#1e2a45]/50 rounded-lg">
                      {update.type === 'price' ? (
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-3 w-3 text-[#3B82F6]" />
                          <span className="text-gray-400">Price update</span>
                          <span className="text-white">
                            {update.prices?.YES_price_formatted || 'N/A'}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <ArrowUpRight className="h-3 w-3 text-green-400" />
                          <span className="text-gray-400">Trade executed</span>
                        </div>
                      )}
                      <span className="text-gray-500 text-[10px]">
                        {new Date(update.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DashboardPage;
